import { useEffect, useState } from 'react';

// Configurator layout breakpoints. Note the visualization stacking is orientation-based (the SCSS
// `configurator-stacked-tablet` mixin), while the chrome (room switcher vs tabs) is width-based below.

/** Subscribe to a media query, SSR-safe (returns false when matchMedia is unavailable). */
export function useMediaMatch(query: string): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' && typeof window.matchMedia === 'function'
            ? window.matchMedia(query).matches
            : false
    );

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
        const mql = window.matchMedia(query);
        const handler = (e: MediaQueryListEvent) => setMatches(e.matches);
        setMatches(mql.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, [query]);

    return matches;
}

// Chrome breakpoint: below 960px the compact chrome (condensed room switcher on top + bottom action
// bar) is used; at/above it the desktop bar with room tabs fits. Chosen so tablets up to and
// including the iPad Mini / Air keep the room switcher, and only genuinely wide viewports show tabs.
const COMPACT_CHROME_QUERY = '(max-width: 959px)';

export const useIsCompactChrome = (): boolean => useMediaMatch(COMPACT_CHROME_QUERY);

// Within the compact chrome, at/above 768px the complete action (sign-and-send / host complete) stays
// in the top bar (next to the total) instead of the bottom action bar; below it (phones) the bottom
// bar carries it. (At/above 960px the desktop tabs bar carries it regardless — see useIsCompactChrome.)
const TOP_BAR_COMPLETE_QUERY = '(min-width: 768px)';

export const useCompleteInTopBar = (): boolean => useMediaMatch(TOP_BAR_COMPLETE_QUERY);

// Portrait phones and tablets stack the visualization on top of the options (SCSS
// `configurator-stacked-tablet` + phone rules), leaving the product-image preview a short, boxed
// area. There the preview drops its title + thumbnail strip and fills with the image as a slider.
const STACKED_PREVIEW_QUERY = '(max-width: 1024px) and (orientation: portrait)';

export const useIsStackedPreview = (): boolean => useMediaMatch(STACKED_PREVIEW_QUERY);
